import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { applyTheme } from "@/lib/themes";
import { CheckCircle2, Download, Package, Sparkles } from "lucide-react";
import ShopNav from "@/components/ShopNav";

const PAYMENTS = [["cod", "Cash on Delivery"], ["upi", "UPI"], ["credit", "Credit Card"], ["debit", "Debit Card"], ["netbanking", "Net Banking"], ["wallet", "Wallet"]];
const FIELDS = [["recipient_name", "Recipient Name", true], ["mobile", "Mobile Number", true], ["email", "Email", false], ["line1", "Address Line 1", true], ["line2", "Address Line 2", false], ["city", "City", true], ["state", "State", true], ["country", "Country", true], ["pincode", "PIN Code", true], ["landmark", "Landmark", false]];

export default function CheckoutPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "demo";
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [useRedeem, setUseRedeem] = useState(false);
  const [payment, setPayment] = useState("cod");
  const [addr, setAddr] = useState({ country: "India" });
  const [placing, setPlacing] = useState(false);
  const [placed, setPlaced] = useState(null);

  const loadQuote = useCallback((ur) => {
    api.post(`/shop/checkout/quote?token=${token}`, { use_redeem: ur }).then((r) => setQuote(r.data)).catch((e) => { toast.error(e?.response?.data?.detail || "Cart empty"); });
  }, [token]);

  useEffect(() => { loadQuote(useRedeem); }, [useRedeem, loadQuote]);
  useEffect(() => { api.get(`/employee/me?token=${token}`).then((r) => { applyTheme(r.data.theme || "standard"); if (r.data.name) setAddr((a) => ({ ...a, recipient_name: r.data.name })); }).catch(() => {}); }, [token]);

  const upd = (k, v) => setAddr((a) => ({ ...a, [k]: v }));
  const validate = () => FIELDS.filter(([k, , req]) => req && !(addr[k] || "").trim()).map(([, l]) => l);

  const place = async () => {
    const missing = validate();
    if (missing.length) { toast.error(`Please fill: ${missing.join(", ")}`); return; }
    setPlacing(true);
    try {
      const r = await api.post(`/shop/orders?token=${token}`, { use_redeem: useRedeem, payment_method: payment, address: addr });
      setPlaced(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Order failed"); } finally { setPlacing(false); }
  };

  if (placed) return <Confirmation token={token} order={placed} navigate={navigate} />;
  if (!quote) return <div className="min-h-screen" style={{ background: "var(--bq-bg)" }}><ShopNav token={token} active="cart" /></div>;

  const free = quote.amount_to_pay === 0;

  return (
    <div className="min-h-screen" style={{ background: "var(--bq-bg)", color: "var(--bq-text)" }}>
      <ShopNav token={token} active="cart" />
      <main className="max-w-5xl mx-auto px-4 md:px-6 py-6">
        <h1 className="text-2xl font-black uppercase mb-4">Checkout</h1>
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <div className="brutal-card p-5">
              <h3 className="font-black uppercase mb-3">Delivery Address</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {FIELDS.map(([k, label, req]) => (
                  <div key={k} className={k === "line1" || k === "line2" ? "sm:col-span-2" : ""}>
                    <label className="text-xs font-black uppercase">{label}{req && " *"}</label>
                    <input data-testid={`addr-${k}`} value={addr[k] || ""} onChange={(e) => upd(k, e.target.value)} className="brutal-input text-sm" />
                  </div>
                ))}
                <div className="sm:col-span-2">
                  <label className="text-xs font-black uppercase">Delivery Instructions (optional)</label>
                  <textarea data-testid="addr-delivery_instructions" value={addr.delivery_instructions || ""} onChange={(e) => upd("delivery_instructions", e.target.value)} className="brutal-input text-sm" rows={2} />
                </div>
              </div>
            </div>

            <div className="brutal-card p-5">
              <label className="flex items-center justify-between gap-2 border-2 border-black p-3 mb-3" style={{ background: quote.redeem_status.eligible ? "var(--bq-card-bg)" : "#f5f5f5" }}>
                <div>
                  <div className="font-black uppercase text-sm">Use Redeem Points</div>
                  <div className="text-xs" style={{ color: "var(--bq-muted)" }}>{quote.redeem_status.eligible ? `Apply ₹${quote.redeem_status.redeem_value} (${quote.redeem_status.available_points} pts available)` : quote.redeem_status.reason}</div>
                </div>
                <input data-testid="use-redeem-toggle" type="checkbox" disabled={!quote.redeem_status.eligible} checked={useRedeem} onChange={(e) => setUseRedeem(e.target.checked)} className="w-5 h-5" />
              </label>

              <h3 className="font-black uppercase mb-2">Payment Method</h3>
              {free ? (
                <div data-testid="payment-skipped" className="border-2 border-black p-3 font-bold" style={{ background: "var(--bq-accent)" }}>🎉 Fully covered by redeem points — no payment needed!</div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {PAYMENTS.map(([v, l]) => (
                    <button key={v} data-testid={`pay-${v}`} onClick={() => setPayment(v)} className={`px-2 py-2 border-2 border-black text-xs font-black uppercase ${payment === v ? "themed-shadow-accent" : ""}`} style={{ background: payment === v ? "var(--bq-accent)" : "var(--bq-card-bg)" }}>{l}</button>
                  ))}
                </div>
              )}
              {!free && (payment === "upi") && <input data-testid="upi-input" placeholder="yourname@upi" className="brutal-input text-sm mt-2" />}
              {!free && (payment === "credit" || payment === "debit") && (
                <div className="grid grid-cols-3 gap-2 mt-2">
                  <input data-testid="card-number" placeholder="Card number" className="brutal-input text-sm col-span-3" />
                  <input placeholder="MM/YY" className="brutal-input text-sm" /><input placeholder="CVV" className="brutal-input text-sm" /><input placeholder="Name" className="brutal-input text-sm" />
                </div>
              )}
              <p className="text-[11px] mt-2" style={{ color: "var(--bq-muted)" }}>Payments are simulated for now (Razorpay coming soon). Your order will be placed instantly.</p>
            </div>
          </div>

          <aside className="brutal-card p-5 h-fit" data-testid="checkout-summary">
            <h3 className="font-black uppercase mb-3">Summary</h3>
            <div className="space-y-1 max-h-48 overflow-y-auto mb-3">
              {quote.lines.map((l, i) => <div key={i} className="flex justify-between text-sm"><span className="truncate pr-2">{l.name} × {l.qty}</span><span className="font-bold">₹{l.line_total}</span></div>)}
            </div>
            <div className="border-t-2 border-black pt-2 space-y-1">
              <SRow l="Subtotal" v={`₹${quote.subtotal}`} />
              {quote.discount_total > 0 && <SRow l="You saved" v={`− ₹${quote.discount_total}`} green />}
              {quote.redeem_value_used > 0 && <SRow l={`Redeem (${quote.points_used} pts)`} v={`− ₹${quote.redeem_value_used}`} green />}
              <div className="border-t-2 border-black my-1" />
              <SRow l="Amount to Pay" v={`₹${quote.amount_to_pay}`} big />
            </div>
            <button data-testid="place-order-btn" disabled={placing} onClick={place} className="brutal-btn w-full mt-4">{placing ? "Placing…" : (free ? "Place Order (Free)" : "Place Order")}</button>
          </aside>
        </div>
      </main>
    </div>
  );
}

function SRow({ l, v, big, green }) {
  return <div className="flex justify-between"><span className={big ? "font-black uppercase" : "text-sm"} style={{ color: green ? "green" : "inherit" }}>{l}</span><span className={big ? "text-xl font-black" : "font-bold"} style={{ color: green ? "green" : "inherit" }}>{v}</span></div>;
}

function Confirmation({ token, order, navigate }) {
  const receipt = () => {
    const lines = order.lines.map((l) => `${l.name} x${l.qty}  ₹${l.line_total}`).join("\n");
    const txt = `TekWissen Merch — Order Receipt\n\nOrder: ${order.order_number}\nDate: ${new Date(order.created_at).toLocaleString()}\nEstimated delivery: ${order.estimated_delivery}\n\n${lines}\n\nSubtotal: ₹${order.subtotal}\nRedeem used: ₹${order.redeem_value_used} (${order.points_used} pts)\nAmount paid: ₹${order.amount_paid}\nPayment: ${order.payment_method}\n\nShip to: ${order.address.recipient_name}, ${order.address.line1}, ${order.address.city} ${order.address.pincode}\n\nThank you for shopping with us!`;
    const blob = new Blob([txt], { type: "text/plain" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `${order.order_number}.txt`; a.click();
  };
  return (
    <div className="min-h-screen" style={{ background: "var(--bq-bg)", color: "var(--bq-text)" }}>
      <ShopNav token={token} active="orders" />
      <main className="max-w-2xl mx-auto px-4 py-10 text-center" data-testid="order-confirmation">
        <CheckCircle2 size={72} className="mx-auto mb-3" style={{ color: "green" }} />
        <h1 className="text-3xl font-black uppercase">🎉 Order Placed Successfully!</h1>
        <p className="mt-2" style={{ color: "var(--bq-muted)" }}>Your order has been received. Your merchandise will be delivered within 7–10 working days. Thank you for shopping with us!</p>
        <div className="brutal-card p-5 mt-5 text-left">
          <div className="flex justify-between font-black uppercase"><span>Order No.</span><span data-testid="order-number">{order.order_number}</span></div>
          <div className="flex justify-between text-sm mt-1"><span>Estimated delivery</span><span>{order.estimated_delivery}</span></div>
          <div className="flex justify-between text-sm mt-1"><span>Amount paid</span><span>₹{order.amount_paid}</span></div>
          {order.points_used > 0 && <div className="flex justify-between text-sm mt-1"><span>Redeem used</span><span>{order.points_used} pts (₹{order.redeem_value_used})</span></div>}
          <div className="border-t-2 border-black my-3" />
          {order.lines.map((l, i) => <div key={i} className="flex justify-between text-sm"><span>{l.name} × {l.qty}</span><span>₹{l.line_total}</span></div>)}
        </div>
        <div className="flex gap-2 mt-4 justify-center">
          <button data-testid="download-receipt" onClick={receipt} className="brutal-btn-secondary"><Download size={14} className="inline mr-1" />Download Receipt</button>
          <button onClick={() => navigate(`/shop/orders?token=${token}`)} className="brutal-btn"><Package size={14} className="inline mr-1" />My Orders</button>
          <button onClick={() => navigate(`/shop?token=${token}`)} className="brutal-btn-secondary"><Sparkles size={14} className="inline mr-1" />Keep Shopping</button>
        </div>
      </main>
    </div>
  );
}
